import React, { useState, useEffect } from 'react';
import GoogleAnalytics from '../../analyticsComponents/GoogleAnalytics';
import logger from '../../utils/logger';
import { GRADE_NAMES, cleanGrade, subjectsForGrade } from '../../utils/catalogue';
import toPascalCase from '../../utils/toPascalCase';
import { NAMING_HINT, generateTitle } from '../../utils/resourceNaming';
import TagInput from './TagInput';
import BulkUpload from './BulkUpload';
import { isDuplicateTitle, logUploadEvent, uploadResource, validateFile } from './uploadResource';

const inputClass =
  'w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-brand-500 focus:border-brand-500 focus:outline-none transition-shadow';

const TABS = [
  { id: 'file', label: 'Document', icon: <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /> },
  { id: 'link', label: 'Link', icon: <path strokeLinecap="round" strokeLinejoin="round" d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" /> },
  { id: 'bulk', label: 'Bulk upload', icon: <path strokeLinecap="round" strokeLinejoin="round" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" /> },
];

const ContributePage = () => {
  const [uploadType, setUploadType] = useState('file');
  const [contributorName, setContributorName] = useState('');
  const [selectedGrade, setSelectedGrade] = useState('');
  const [subject, setSubject] = useState('');
  const [file, setFile] = useState(null);
  const [linkURL, setLinkURL] = useState('');
  const [theme, setTheme] = useState('');
  // REQ-04: the title is generated from Grade – Subject – Theme until the
  // uploader edits it
  const [title, setTitle] = useState('');
  const [titleEdited, setTitleEdited] = useState(false);
  const [tags, setTags] = useState([]);
  const [uploadProg, setUploadProg] = useState(0);
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [successModal, setSuccessModal] = useState(false);
  const [lastContributor, setLastContributor] = useState('');
  const [isUploading, setIsUploading] = useState(false);

  const subjects = subjectsForGrade(selectedGrade);
  const suggestedTitle = generateTitle({ gradeName: selectedGrade, subjectName: subject, theme });
  const effectiveTitle = titleEdited ? title : suggestedTitle;
  const isSecureLink = (() => {
    try {
      return new URL(linkURL).protocol === 'https:';
    } catch {
      return false;
    }
  })();

  useEffect(() => {
    document.body.classList.add('contribute-active');
    return () => document.body.classList.remove('contribute-active');
  }, []);

  const handleFileChange = (e) => {
    const selected = e.target.files[0];
    if (!selected) return;
    const problem = validateFile(selected);
    setError(problem);
    setFile(problem ? null : selected);
  };

  const resetForm = () => {
    setUploadProg(0);
    setSubject('');
    setFile(null);
    setLinkURL('');
    setTheme('');
    setTitle('');
    setTitleEdited(false);
    setTags([]);
    setSelectedGrade('');
    setError('');
    setFieldErrors({});
    setIsUploading(false);
  };

  const uploadHandler = async (e) => {
    e.preventDefault();
    const errors = {
      grade: !selectedGrade,
      subject: !subject,
      theme: !theme.trim(),
      title: !effectiveTitle.trim(),
      tags: tags.length === 0,
      file: uploadType === 'file' && !file,
      link: uploadType === 'link' && (!linkURL || !isSecureLink),
    };
    setFieldErrors(errors);
    if (Object.values(errors).some(Boolean)) {
      setError('Please fill in all required fields.');
      return;
    }

    setError('');
    setIsUploading(true);
    const grade = cleanGrade(selectedGrade);
    const storeSubject = toPascalCase(subject);

    try {
      if (await isDuplicateTitle(grade, storeSubject, effectiveTitle)) {
        setError('A resource with this name already exists for this grade and subject. Please choose a different name.');
        setIsUploading(false);
        return;
      }
      await uploadResource({
        grade,
        subject: storeSubject,
        title: effectiveTitle,
        theme,
        tags,
        contributor: contributorName,
        file: uploadType === 'file' ? file : null,
        linkURL: uploadType === 'link' ? linkURL : null,
        onProgress: setUploadProg,
      });
      await logUploadEvent({ subject, title: effectiveTitle, contributor: contributorName });
      setLastContributor(contributorName.trim() || 'anonymous');
      setSuccessModal(true);
      resetForm();
    } catch (err) {
      logger.error(err);
      setError(
        err?.code === 'permission-denied'
          ? 'You do not have permission to upload resources.'
          : 'The resource could not be uploaded for moderation. Please try again.'
      );
      setIsUploading(false);
      setUploadProg(0);
    }
  };

  return (
    <div className="min-h-screen w-full bg-gray-50 pb-16">
      <GoogleAnalytics page="FileUploadPage" eventType="fileUpload_page" />

      {/* Page header */}
      <div className={`${uploadType === 'bulk' ? 'max-w-6xl' : 'max-w-2xl'} mx-auto px-4 sm:px-6 pt-10 pb-2`}>
        <h1 className="text-2xl font-bold text-gray-900 tracking-tight">Contribute a Resource</h1>
        <p className="mt-1 text-sm text-gray-500">Upload a document or link so other educators can benefit.</p>
      </div>

      <div className={`${uploadType === 'bulk' ? 'max-w-6xl' : 'max-w-2xl'} mx-auto px-4 sm:px-6 mt-6`}>
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          {/* Upload type toggle */}
          <div className="flex border-b border-gray-100">
            {TABS.map((tab) => (
              <button
                key={tab.id}
                type="button"
                onClick={() => { if (!isUploading) { setUploadType(tab.id); setError(''); } }}
                className={`flex-1 py-3 text-sm font-medium text-center transition-colors ${
                  uploadType === tab.id
                    ? 'text-brand-600 border-b-2 border-brand-600 bg-brand-50/40'
                    : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                }`}
              >
                <span className="inline-flex items-center gap-1.5">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>{tab.icon}</svg>
                  {tab.label}
                </span>
              </button>
            ))}
          </div>

          {uploadType === 'bulk' ? (
            <BulkUpload />
          ) : (
            <form onSubmit={uploadHandler} className="p-6 space-y-5">
              {/* Contributor name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Contributor Name <span className="text-gray-400 font-normal">(optional)</span>
                </label>
                <input
                  type="text"
                  placeholder="Anonymous"
                  value={contributorName}
                  onChange={(e) => setContributorName(e.target.value)}
                  className={inputClass}
                />
              </div>

              {/* Grade & Subject row */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Grade</label>
                  <select
                    onChange={(e) => { setSelectedGrade(e.target.value); setSubject(''); }}
                    value={selectedGrade}
                    className={`${inputClass} bg-white ${fieldErrors.grade ? 'border-red-400 ring-2 ring-red-100' : ''}`}
                  >
                    <option value="">Select Grade</option>
                    {GRADE_NAMES.map((g) => <option key={g} value={g}>{g}</option>)}
                  </select>
                  {fieldErrors.grade && <p className="text-red-500 text-xs mt-1">Please select a grade</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Subject</label>
                  <select
                    value={subject}
                    onChange={(e) => setSubject(e.target.value)}
                    disabled={!selectedGrade}
                    className={`${inputClass} bg-white ${!selectedGrade ? 'opacity-50 cursor-not-allowed' : ''} ${fieldErrors.subject ? 'border-red-400 ring-2 ring-red-100' : ''}`}
                  >
                    <option value="" disabled>Select Subject</option>
                    {subjects.map((subj) => <option key={subj} value={subj}>{subj}</option>)}
                  </select>
                  {fieldErrors.subject && <p className="text-red-500 text-xs mt-1">Please select a subject</p>}
                </div>
              </div>

              {/* File or Link input */}
              {uploadType === 'file' ? (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">File</label>
                  <label className={`flex flex-col items-center justify-center w-full h-32 rounded-lg border-2 border-dashed bg-gray-50 hover:bg-gray-100 hover:border-gray-400 transition-colors cursor-pointer ${fieldErrors.file ? 'border-red-400' : 'border-gray-300'}`}>
                    <div className="flex flex-col items-center text-center px-4">
                      {file ? (
                        <>
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-brand-600 mb-1" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                          <p className="text-sm font-medium text-gray-700 truncate max-w-[250px]">{file.name}</p>
                          <p className="text-xs text-gray-400 mt-0.5">{(file.size / 1024 / 1024).toFixed(1)} MB · Click to change</p>
                        </>
                      ) : (
                        <>
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-gray-400 mb-1" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" /></svg>
                          <p className="text-sm text-gray-500"><span className="font-medium text-brand-600">Click to upload</span> a file</p>
                          <p className="text-xs text-gray-400 mt-0.5">PDF, DOC, DOCX, PPT, PPTX · Max 10 MB</p>
                        </>
                      )}
                    </div>
                    <input type="file" onChange={handleFileChange} className="hidden" />
                  </label>
                </div>
              ) : (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Link URL</label>
                  <input
                    type="url"
                    placeholder="https://example.com/resource"
                    value={linkURL}
                    onChange={(e) => setLinkURL(e.target.value.trim())}
                    className={`${inputClass} ${
                      !linkURL ? '' : isSecureLink ? 'border-green-400 bg-green-50/50' : 'border-red-400'
                    }`}
                  />
                  {linkURL && !isSecureLink && <p className="text-red-500 text-xs mt-1">Link must use HTTPS</p>}
                </div>
              )}

              {/* Theme */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Theme</label>
                <input
                  type="text"
                  placeholder="e.g. Fractions, Photosynthesis, Term 2 revision"
                  value={theme}
                  onChange={(e) => setTheme(e.target.value)}
                  className={`${inputClass} ${fieldErrors.theme ? 'border-red-400 ring-2 ring-red-100' : ''}`}
                />
                {fieldErrors.theme && <p className="text-red-500 text-xs mt-1">Please give the topic or theme this resource covers</p>}
              </div>

              {/* Title (REQ-04) */}
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="block text-sm font-medium text-gray-700">Document name</label>
                  {titleEdited && (
                    <button type="button" onClick={() => { setTitleEdited(false); setTitle(''); }} className="text-xs font-medium text-brand-600 hover:text-brand-700">
                      Use suggested name
                    </button>
                  )}
                </div>
                <input
                  type="text"
                  placeholder="Filled in from grade, subject and theme"
                  value={effectiveTitle}
                  onChange={(e) => { setTitle(e.target.value); setTitleEdited(true); }}
                  className={`${inputClass} ${fieldErrors.title ? 'border-red-400 ring-2 ring-red-100' : ''}`}
                />
                <p className="text-xs text-gray-400 mt-1.5">Naming convention: {NAMING_HINT}. You can change the suggested name.</p>
              </div>

              {/* Tags */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Tags</label>
                <TagInput tags={tags} onChange={(next) => { setTags(next); setFieldErrors((f) => ({ ...f, tags: false })); }} error={fieldErrors.tags} />
                <p className="text-xs text-gray-400 mt-1.5">Add keywords so others can find your resource. Press Enter to add.</p>
                {fieldErrors.tags && <p className="text-red-500 text-xs mt-1">Add at least one tag</p>}
              </div>

              {/* Error message */}
              {error && (
                <div className="flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                  {error}
                </div>
              )}

              {/* Progress bar */}
              {uploadProg > 0 && uploadProg < 100 && (
                <div>
                  <div className="flex items-center justify-between text-xs text-gray-500 mb-1">
                    <span>Uploading…</span>
                    <span>{Math.round(uploadProg)}%</span>
                  </div>
                  <div className="w-full h-1.5 bg-gray-200 rounded-full overflow-hidden">
                    <div className="h-full bg-brand-600 rounded-full transition-all duration-300 ease-out" style={{ width: `${uploadProg}%` }} />
                  </div>
                </div>
              )}

              {/* Submit */}
              <button
                type="submit"
                disabled={isUploading}
                className={`w-full py-2.5 rounded-lg text-sm font-medium text-white transition-colors ${
                  isUploading ? 'bg-brand-400 cursor-not-allowed' : 'bg-brand-600 hover:bg-brand-700'
                }`}
              >
                {isUploading
                  ? 'Uploading…'
                  : uploadType === 'link' ? 'Upload Link Resource' : 'Upload Document Resource'}
              </button>
            </form>
          )}
        </div>
      </div>

      {/* Success Modal */}
      {successModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 px-4">
          <div className="bg-white rounded-xl shadow-xl max-w-sm w-full p-6 text-center">
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-green-100 text-green-600 mb-3">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
            </div>
            <h3 className="text-base font-semibold text-gray-900">Resource Uploaded</h3>
            <p className="text-sm text-gray-500 mt-1">Contributed by <span className="font-medium text-gray-700">{lastContributor}</span>. It will be reviewed by a moderator.</p>
            <button
              onClick={() => setSuccessModal(false)}
              className="mt-4 w-full py-2 rounded-lg text-sm font-medium text-white bg-brand-600 hover:bg-brand-700 transition-colors"
            >
              Done
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ContributePage;
